import useAutoUpdatingRef from 'hooks/useAutoUpdatingRef'
import { useCallback, useEffect, useRef, useState, type RefObject } from 'react'
import type { MessagePart } from './messageTypes'
import {
  getChatLinkOfferClickData,
  isHoverPreviewOfferType,
  type ChatLinkHoverTarget,
} from './ChatLinkHoverPreview'
import { HOVER_CLOSE_DELAY_MS } from './PlaceHoverPreview/constants'
import type { LinkRect } from './PlaceHoverPreview/types'

interface UseChatLinkInteractionsOptions {
  markdownContainerRef: RefObject<HTMLDivElement | null>
  canShowHover: boolean
  contentKey: string
  failedPlaceQueries: ReadonlySet<string>
  messageId?: string
  onOfferClick: (data: { offerId: string; offerType?: string }) => void
  onPlaceClick: (data: { googlePlaceId: string; placeName: string }) => void
  onPlaceSearchClick: (data: { searchQuery: string; placeName: string }) => void
}

interface OfferClickData {
  offerId: string
  offerType?: string
}

function getLinkRect(element: Element): LinkRect {
  const rect = element.getBoundingClientRect()
  return {
    top: rect.top,
    bottom: rect.bottom,
    left: rect.left,
    right: rect.right,
    width: rect.width,
    height: rect.height,
  }
}

function getOfferClickData(target: HTMLElement): OfferClickData | undefined {
  const offerId = target.dataset.offerId
  if (!offerId) {
    return undefined
  }

  return getChatLinkOfferClickData(offerId, target.dataset.offerType)
}

function getChatLinkTarget(event: Event): HTMLElement | undefined {
  const target = event.target
  if (!(target instanceof Element)) {
    return undefined
  }

  const placeTag = target.closest('.place-tag')
  if (placeTag instanceof HTMLElement) {
    return placeTag
  }

  const offerTag = target.closest('.offer-tag')
  if (offerTag instanceof HTMLElement) {
    return offerTag
  }

  return undefined
}

/** Stable key for markdown link wiring — tracks rendered text, not just parts array identity. */
export function getChatLinkContentKey(parts: ReadonlyArray<MessagePart>): string {
  return parts.map(part => {
    if (part.type === 'text') {
      return part.content
    }
    return JSON.stringify(part)
  }).join('\x1e')
}

export function useChatLinkInteractions({
  markdownContainerRef,
  canShowHover,
  contentKey,
  failedPlaceQueries,
  messageId,
  onOfferClick,
  onPlaceClick,
  onPlaceSearchClick,
}: UseChatLinkInteractionsOptions) {
  const [hoverTarget, setHoverTarget] = useState<ChatLinkHoverTarget | undefined>()
  const hoverCloseTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const onOfferClickRef = useAutoUpdatingRef(onOfferClick)
  const onPlaceClickRef = useAutoUpdatingRef(onPlaceClick)
  const onPlaceSearchClickRef = useAutoUpdatingRef(onPlaceSearchClick)
  const failedPlaceQueriesRef = useAutoUpdatingRef(failedPlaceQueries)

  const clearHoverCloseTimeout = useCallback(() => {
    if (hoverCloseTimeoutRef.current) {
      clearTimeout(hoverCloseTimeoutRef.current)
      hoverCloseTimeoutRef.current = undefined
    }
  }, [])

  const openHoverTarget = useCallback((target: ChatLinkHoverTarget) => {
    clearHoverCloseTimeout()
    setHoverTarget(target)
  }, [clearHoverCloseTimeout])

  const scheduleHoverClose = useCallback(() => {
    clearHoverCloseTimeout()
    hoverCloseTimeoutRef.current = setTimeout(() => {
      setHoverTarget(undefined)
      hoverCloseTimeoutRef.current = undefined
    }, HOVER_CLOSE_DELAY_MS)
  }, [clearHoverCloseTimeout])

  const closeHoverTarget = useCallback(() => {
    clearHoverCloseTimeout()
    setHoverTarget(undefined)
  }, [clearHoverCloseTimeout])

  useEffect(() => {
    return () => clearHoverCloseTimeout()
  }, [clearHoverCloseTimeout])

  useEffect(() => {
    clearHoverCloseTimeout()
    setHoverTarget(undefined)
  }, [
    canShowHover,
    clearHoverCloseTimeout,
    contentKey,
    failedPlaceQueries,
    messageId,
  ])

  useEffect(() => {
    const container = markdownContainerRef.current
    if (!container) return

    const isFailedPlace = (target: HTMLElement) => target.classList.contains('place-tag--failed') ||
      !!(target.dataset.placeSearch && failedPlaceQueriesRef.current.has(target.dataset.placeSearch))

    const activateTag = (target: HTMLElement) => {
      if (target.classList.contains('place-tag')) {
        if (isFailedPlace(target)) return
        const placeId = target.dataset.placeId
        const searchQuery = target.dataset.placeSearch
        const placeName = target.textContent || target.dataset.placeName || ''
        if (placeId) {
          onPlaceClickRef.current({ googlePlaceId: placeId, placeName })
        } else if (searchQuery) {
          onPlaceSearchClickRef.current({ searchQuery, placeName })
        }
        return
      }
      const data = getOfferClickData(target)
      if (data) onOfferClickRef.current(data)
    }

    const handleClick = (event: Event) => {
      const target = getChatLinkTarget(event)
      if (target && container.contains(target)) activateTag(target)
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Enter' && event.key !== ' ') return
      const target = getChatLinkTarget(event)
      if (!target || !container.contains(target)) return
      event.preventDefault()
      activateTag(target)
    }

    const handleMouseOver = (event: MouseEvent) => {
      if (!canShowHover) return
      const target = getChatLinkTarget(event)
      if (!target || !container.contains(target) ||
        (event.relatedTarget instanceof Node && target.contains(event.relatedTarget))) return
      if (target.classList.contains('place-tag')) {
        if (isFailedPlace(target)) return
        const placeId = target.dataset.placeId
        const searchQuery = target.dataset.placeSearch
        const placeName = target.textContent || target.dataset.placeName || ''
        if (placeId) {
          openHoverTarget({ type: 'place', placeId, placeName, rect: getLinkRect(target) })
        } else if (searchQuery) {
          openHoverTarget({ type: 'placeSearch', searchQuery, placeName, rect: getLinkRect(target) })
        }
        return
      }
      const { offerId, offerType } = target.dataset
      if (offerId && isHoverPreviewOfferType(offerType)) {
        openHoverTarget({
          type: 'offer',
          offerId,
          offerType,
          offerName: target.textContent || target.dataset.offerName || '',
          rect: getLinkRect(target),
        })
      }
    }

    const handleMouseOut = (event: MouseEvent) => {
      if (!canShowHover) return
      const target = getChatLinkTarget(event)
      if (!target || !container.contains(target) ||
        (event.relatedTarget instanceof Node && target.contains(event.relatedTarget))) return
      scheduleHoverClose()
    }

    container.addEventListener('click', handleClick)
    container.addEventListener('keydown', handleKeyDown)
    container.addEventListener('mouseover', handleMouseOver)
    container.addEventListener('mouseout', handleMouseOut)
    return () => {
      container.removeEventListener('click', handleClick)
      container.removeEventListener('keydown', handleKeyDown)
      container.removeEventListener('mouseover', handleMouseOver)
      container.removeEventListener('mouseout', handleMouseOut)
    }
  }, [canShowHover, contentKey, markdownContainerRef, messageId, onOfferClickRef, onPlaceClickRef,
    onPlaceSearchClickRef, failedPlaceQueriesRef, openHoverTarget, scheduleHoverClose])

  useEffect(() => {
    const container = markdownContainerRef.current
    if (!container) return

    const updatePlaceTag = (tag: Element) => {
      if (!(tag instanceof HTMLElement)) return
      const query = tag.dataset.placeSearch
      if (!query && !tag.dataset.placeId) return
      const isFailed = !!query && failedPlaceQueries.has(query)
      tag.classList.toggle('place-tag--failed', isFailed)
      tag.setAttribute('role', 'button')
      tag.tabIndex = isFailed ? -1 : 0
      if (isFailed) tag.setAttribute('aria-disabled', 'true')
      else tag.removeAttribute('aria-disabled')
    }
    const updateSubtree = (root: Element) => {
      if (root.matches('.place-tag')) updatePlaceTag(root)
      root.querySelectorAll('.place-tag').forEach(updatePlaceTag)
    }
    updateSubtree(container)
    const observer = new MutationObserver(records => {
      for (const record of records) {
        record.addedNodes.forEach(node => {
          if (node instanceof Element) updateSubtree(node)
        })
      }
    })
    observer.observe(container, { childList: true, subtree: true })
    return () => observer.disconnect()
  }, [contentKey, failedPlaceQueries, markdownContainerRef, messageId])

  return {
    hoverTarget,
    closeHoverTarget,
    keepHoverTargetOpen: clearHoverCloseTimeout,
  }
}
